import { z } from "zod"

import { foundationProjectionReadV1Schema } from "./foundation-projection-v1.js"
import {
  branchIdSchema,
  correlationIdSchema,
  legalEntityIdSchema,
  membershipIdSchema,
  tenantIdSchema,
} from "./identifiers.js"
import {
  capabilityKeyV1Schema,
  membershipPersonaV1Schema,
} from "./membership-v1.js"

export const legalEntityChoiceV1Schema = z.object({
  legalEntityId: legalEntityIdSchema,
  name: z.string().min(1).max(120),
}).strict()

export const branchChoiceV1Schema = z.object({
  branchId: branchIdSchema,
  legalEntityId: legalEntityIdSchema,
  name: z.string().min(1).max(120),
}).strict()

export const tenantChoiceV1Schema = z
  .object({
    membershipId: membershipIdSchema,
    tenantId: tenantIdSchema,
    name: z.string().min(1).max(80),
    location: z.string().min(1).max(80),
    available: z.boolean(),
  })
  .strict()

export const foundationConsoleSessionV1Schema = z
  .object({
    sessionVersion: z.literal(1),
    displayName: z.string().min(1).max(120),
    selectedMembershipId: membershipIdSchema,
    selectedTenantId: tenantIdSchema,
    persona: membershipPersonaV1Schema.default("administrator"),
    capabilities: z.array(capabilityKeyV1Schema).max(256).default([]),
    permissionVersion: z.number().int().positive().default(1),
    selectedLegalEntityId: legalEntityIdSchema.nullable().default(null),
    selectedBranchId: branchIdSchema.nullable().default(null),
    legalEntities: z.array(legalEntityChoiceV1Schema).max(100).default([]),
    branches: z.array(branchChoiceV1Schema).max(500).default([]),
    tenants: z.array(tenantChoiceV1Schema).min(1).max(20),
  })
  .strict()
  .superRefine((session, context) => {
    if (!session.tenants.some((tenant) =>
      tenant.membershipId === session.selectedMembershipId &&
      tenant.tenantId === session.selectedTenantId
    )) {
      context.addIssue({
        code: "custom",
        path: ["selectedTenantId"],
        message: "Selected Tenant and Membership must be available in this session.",
      })
    }
    if (
      session.selectedLegalEntityId !== null
      && !session.legalEntities.some(
        (choice) => choice.legalEntityId === session.selectedLegalEntityId
      )
    ) {
      context.addIssue({
        code: "custom",
        path: ["selectedLegalEntityId"],
        message: "Selected Legal Entity must be available in this session.",
      })
    }
    if (session.selectedBranchId !== null) {
      const selectedBranch = session.branches.find(
        (choice) => choice.branchId === session.selectedBranchId
      )
      if (
        selectedBranch === undefined
        || (
          session.selectedLegalEntityId !== null
          && selectedBranch.legalEntityId !== session.selectedLegalEntityId
        )
      ) {
        context.addIssue({
          code: "custom",
          path: ["selectedBranchId"],
          message: "Selected Branch must belong to the selected Legal Entity.",
        })
      }
    }
  })

export const foundationMutationRequestV1Schema = z
  .object({
    correlationId: correlationIdSchema,
    idempotencyKey: z.string().min(8).max(200),
    expectedVersion: z.number().int().nonnegative(),
    targetState: z.enum(["active", "paused"]),
  })
  .strict()

const mutationStatusV1Schema = z.enum([
  "confirmed",
  "conflict",
  "denied",
  "retry",
])

export const foundationMutationResponseV1Schema = z
  .object({
    responseVersion: z.literal(1),
    status: mutationStatusV1Schema,
    correlationId: correlationIdSchema,
    auditStatus: z.enum(["recorded", "not_recorded"]),
    projection: foundationProjectionReadV1Schema.nullable(),
  })
  .strict()

export type TenantChoiceV1 = z.infer<typeof tenantChoiceV1Schema>
export type LegalEntityChoiceV1 = z.infer<typeof legalEntityChoiceV1Schema>
export type BranchChoiceV1 = z.infer<typeof branchChoiceV1Schema>
export type FoundationConsoleSessionV1 = z.infer<typeof foundationConsoleSessionV1Schema>
export type FoundationMutationRequestV1 = z.infer<typeof foundationMutationRequestV1Schema>
export type FoundationMutationResponseV1 = z.infer<typeof foundationMutationResponseV1Schema>
